import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './common/hooks/useAuth';
import LoginScreen from './screens/auth/LoginScreen';
import RegisterProducerScreen from './screens/auth/RegisterProducerScreen';
import DashboardScreen from './screens/dashboard/DashboardScreen';
import FarmDetailsScreen from './screens/producers/FarmDetailsScreen';
import ProductsScreen from './screens/products/ProductsScreen';
import AddProductScreen from './screens/products/AddProductScreen';
import StockScreen from './screens/products/StockScreen';
import OrdersScreen from './screens/orders/OrdersScreen';
import AddOrderScreen from './screens/orders/AddOrderScreen';
import OrderDetailsScreen from './screens/orders/OrderDetailsScreen';
import Loading from './common/components/Loading';
import Header from './common/components/Header';
import Drawer from './common/components/Drawer';
import { Home, Package, BarChart3, ShoppingCart, MapPin, LogOut } from 'lucide-react';
import { useProducer } from './common/hooks/useProducer';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading message="Authenticating..." />;
  }

  return user ? children : <Navigate to="/login" />;
};

// Component to handle root path and redirect to producer dashboard
const RootRedirect = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: producerLoading } = useProducer();

  if (authLoading || producerLoading) {
    return <Loading message="Loading..." />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!profile) {
    return <Navigate to="/register" />;
  }

  return <Navigate to={`/${profile.url_friendly_name}`} replace />;
};

const ProducerRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: producerLoading } = useProducer();
  const location = useLocation();
  const params = useParams();

  if (authLoading || producerLoading) {
    return <Loading message="Loading..." />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If profile doesn't exist, redirect to register
  if (!profile) {
    return <Navigate to="/register" />;
  }

  // If we have a profile but the URL doesn't match, redirect to correct URL
  if (params.urlFriendlyName && params.urlFriendlyName !== profile.url_friendly_name) {
    const newPath = location.pathname.replace(`/${params.urlFriendlyName}/`, `/${profile.url_friendly_name}/`);
    return <Navigate to={newPath} replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { profile } = useProducer();
  const location = useLocation();
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);

  // Don't show layout for login and registration pages
  if (!user || location.pathname === '/register' || location.pathname === '/login') {
    return children;
  }

  // Don't show navigation if profile not loaded yet
  if (!profile) {
    return children;
  }

  const baseUrl = `/${profile.url_friendly_name}`;
  const navigationItems = [
    { path: baseUrl, label: 'Dashboard', icon: Home },
    { path: `${baseUrl}/farm-details`, label: 'Farm Details', icon: MapPin },
    { path: `${baseUrl}/products`, label: 'Products', icon: Package },
    { path: `${baseUrl}/stock`, label: 'Stock', icon: BarChart3 },
    { path: `${baseUrl}/orders`, label: 'Orders', icon: ShoppingCart },
  ];

  const handleLogout = () => {
    setAccountDrawerOpen(false);
    logout();
  };

  return (
    <div className="app-layout">
      <Header
        onMenuClick={() => setNavDrawerOpen(true)}
        onAccountClick={() => setAccountDrawerOpen(true)}
      />

      <Drawer
        isOpen={navDrawerOpen}
        onClose={() => setNavDrawerOpen(false)}
        position="left"
        title="Navigation"
      >
        <nav>
          <ul className="nav-menu">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path} className="nav-menu-item">
                  <Link
                    to={item.path}
                    className={`nav-menu-link ${isActive ? 'active' : ''}`}
                    onClick={() => setNavDrawerOpen(false)}
                  >
                    <Icon size={20} className="nav-menu-icon" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </Drawer>

      <Drawer
        isOpen={accountDrawerOpen}
        onClose={() => setAccountDrawerOpen(false)}
        position="right"
        title="Account"
      >
        <div className="account-info">
          <div className="account-info-label">Signed in as</div>
          <div className="account-info-value">{user.email}</div>
        </div>
        <ul className="account-menu">
          <li className="account-menu-item">
            <button className="account-menu-button danger" onClick={handleLogout}>
              <LogOut size={20} className="account-menu-icon" />
              Logout
            </button>
          </li>
        </ul>
      </Drawer>

      <main className="app-main">
        <div className="app-main-container">
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppLayout>
            <Routes>
              <Route path="/login" element={<LoginScreen />} />
              <Route
                path="/register"
                element={
                  <PrivateRoute>
                    <RegisterProducerScreen />
                  </PrivateRoute>
                }
              />
              <Route path="/" element={<RootRedirect />} />
              <Route
                path="/:urlFriendlyName"
                element={
                  <ProducerRoute>
                    <DashboardScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/farm-details"
                element={
                  <ProducerRoute>
                    <FarmDetailsScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/products"
                element={
                  <ProducerRoute>
                    <ProductsScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/products/add"
                element={
                  <ProducerRoute>
                    <AddProductScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/products/edit/:id"
                element={
                  <ProducerRoute>
                    <AddProductScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/stock"
                element={
                  <ProducerRoute>
                    <StockScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/orders"
                element={
                  <ProducerRoute>
                    <OrdersScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/orders/add"
                element={
                  <ProducerRoute>
                    <AddOrderScreen />
                  </ProducerRoute>
                }
              />
              <Route
                path="/:urlFriendlyName/orders/:id"
                element={
                  <ProducerRoute>
                    <OrderDetailsScreen />
                  </ProducerRoute>
                }
              />
            </Routes>
          </AppLayout>
        </Router>
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
